// Brokerage Window - Tier 2 users only (RBAC gated)
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import api from '@/lib/api';
import { toast } from 'sonner';

export const BrokerageWindow = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchSymbol, setSearchSymbol] = useState('');
  const [searchResult, setSearchResult] = useState(null);
  const [prohibitedList, setProhibitedList] = useState([]);
  const [watchlist, setWatchlist] = useState([]);
  const [recentOrders, setRecentOrders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  
  // Order form state
  const [orderSide, setOrderSide] = useState('buy');
  const [orderQuantity, setOrderQuantity] = useState('');
  const [orderType, setOrderType] = useState('market');
  const [limitPrice, setLimitPrice] = useState('');
  const [timeInForce, setTimeInForce] = useState('day');
  const [showWarningModal, setShowWarningModal] = useState(false);
  const [pendingOrder, setPendingOrder] = useState(null);
  const [ogeWarnings, setOgeWarnings] = useState([]);

  useEffect(() => {
    // RBAC check - Tier 2 only
    if (user?.role && !['Operations', 'Compliance'].includes(user.role)) {
      navigate('/dashboard');
      return;
    }
    
    loadInitialData();
  }, [user, navigate]);

  const loadInitialData = async () => {
    try {
      const [prohibitedRes, ordersRes] = await Promise.all([
        api.get('/compliance/prohibited'),
        api.get('/investments/orders')  // Get recent orders
      ]);

      setProhibitedList(prohibitedRes.data.prohibited_securities || []);
      
      // Filter to Tier 2 orders only
      const tier2Orders = (ordersRes.data.orders || [])
        .filter(o => o.tier === 2)
        .slice(0, 10);
      setRecentOrders(tier2Orders);

      // Load watchlist from localStorage
      const savedWatchlist = localStorage.getItem('tier2_watchlist');
      if (savedWatchlist) {
        setWatchlist(JSON.parse(savedWatchlist));
      }

    } catch (err) {
      console.error('Error loading brokerage data:', err);
    }
  };

  const handleSearch = async () => {
    if (!searchSymbol.trim()) {
      toast.error('Please enter a symbol');
      return;
    }

    try {
      setLoading(true);
      setError('');
      setSearchResult(null);

      // Get quote from Polygon via backend
      // For now, use a mock quote endpoint or Tradier
      const response = await api.get(`/market/quote/${searchSymbol.toUpperCase()}`);
      
      setSearchResult({
        symbol: searchSymbol.toUpperCase(),
        ...response.data
      });

    } catch (err) {
      setError(err.response?.data?.detail || `Failed to get quote for ${searchSymbol}`);
      toast.error(`Symbol not found: ${searchSymbol}`);
    } finally {
      setLoading(false);
    }
  };

  const isProhibited = (symbol) => {
    return prohibitedList.some(p => p.symbol === symbol.toUpperCase());
  };

  const getProhibitedInfo = (symbol) => {
    return prohibitedList.find(p => p.symbol === symbol.toUpperCase());
  };

  const addToWatchlist = (symbol) => {
    if (!watchlist.some(w => w.symbol === symbol)) {
      const newWatchlist = [...watchlist, { symbol, addedAt: new Date().toISOString() }];
      setWatchlist(newWatchlist);
      localStorage.setItem('tier2_watchlist', JSON.stringify(newWatchlist));
      toast.success(`${symbol} added to watchlist`);
    }
  };

  const removeFromWatchlist = (symbol) => {
    const newWatchlist = watchlist.filter(w => w.symbol !== symbol);
    setWatchlist(newWatchlist);
    localStorage.setItem('tier2_watchlist', JSON.stringify(newWatchlist));
    toast.success(`${symbol} removed from watchlist`);
  };

  const handlePlaceOrder = async () => {
    if (!searchResult) {
      toast.error('Please search for a security first');
      return;
    }

    if (!orderQuantity || parseFloat(orderQuantity) <= 0) {
      toast.error('Please enter a valid quantity');
      return;
    }

    if (orderType === 'limit' && (!limitPrice || parseFloat(limitPrice) <= 0)) {
      toast.error('Please enter a valid limit price');
      return;
    }

    // Check if prohibited
    if (isProhibited(searchResult.symbol)) {
      toast.error('This security is prohibited by OGE regulations');
      return;
    }

    // Prepare order
    const order = {
      symbol: searchResult.symbol,
      quantity: parseFloat(orderQuantity),
      side: orderSide,
      order_type: orderType,
      limit_price: orderType === 'limit' ? parseFloat(limitPrice) : null,
      time_in_force: timeInForce
    };

    try {
      setLoading(true);

      // Place order via Tier 2 endpoint (includes OGE check)
      const response = await api.post('/compliance/tier2/order', order);

      // Check if there are warnings
      const complianceResult = response.data.compliance_result;
      if (complianceResult && complianceResult.warnings && complianceResult.warnings.length > 0) {
        // Show warning modal
        setPendingOrder(order);
        setOgeWarnings(complianceResult.warnings);
        setShowWarningModal(true);
        setLoading(false);
        return;
      }

      // Order placed successfully
      toast.success(`Order placed: ${orderSide.toUpperCase()} ${orderQuantity} ${searchResult.symbol}`);
      
      // Reset form
      setOrderQuantity('');
      setLimitPrice('');
      
      // Reload orders
      loadInitialData();

    } catch (err) {
      const errorDetail = err.response?.data?.detail;
      
      if (errorDetail && errorDetail.compliance_result) {
        // OGE blocked
        toast.error(`Order blocked: ${errorDetail.compliance_result.reason}`);
      } else {
        toast.error(errorDetail || 'Failed to place order');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleAcknowledgeWarnings = async () => {
    // User acknowledged warnings, proceed with order
    setShowWarningModal(false);
    
    // Note: In real implementation, you might want to add an "acknowledged_warnings" flag
    // For now, warnings were already recorded in the first submission
    
    toast.success('Order acknowledged and placed');
    setOrderQuantity('');
    setLimitPrice('');
    setPendingOrder(null);
    setOgeWarnings([]);
    loadInitialData();
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount || 0);
  };

  const formatNumber = (num) => {
    return new Intl.NumberFormat('en-US').format(num || 0);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const prohibited = searchResult && isProhibited(searchResult.symbol);
  const prohibitedInfo = prohibited ? getProhibitedInfo(searchResult.symbol) : null;

  return (
    <div className="min-h-screen bg-[#F7F8FA]">
      {/* Header */}
      <div className="bg-white border-b border-[#E1E4E8]">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <h1 className="text-2xl font-bold text-[#24292E]">Tier 2 Brokerage Window</h1>
          <p className="text-sm text-[#586069] mt-1">
            Full market access with OGE compliance enforcement
          </p>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left Column - Search & Order Entry */}
          <div className="lg:col-span-2 space-y-6">
            {/* Search Bar */}
            <div className="bg-white rounded-lg border border-[#E1E4E8] p-6">
              <h2 className="text-lg font-semibold text-[#24292E] mb-4">Symbol Search</h2>
              
              <div className="flex gap-3">
                <input
                  type="text"
                  placeholder="Enter symbol (e.g., AAPL, MSFT)"
                  value={searchSymbol}
                  onChange={(e) => setSearchSymbol(e.target.value.toUpperCase())}
                  onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                  className="flex-1 px-4 py-3 border border-[#D1D5DB] rounded text-lg font-mono uppercase focus:outline-none focus:ring-2 focus:ring-[#0366D6] focus:border-transparent"
                />
                <button
                  onClick={handleSearch}
                  disabled={loading}
                  className="px-6 py-3 bg-[#0366D6] hover:bg-[#0256C5] disabled:bg-[#D1D5DB] text-white font-semibold rounded transition-colors"
                >
                  {loading ? 'Searching...' : 'Search'}
                </button>
              </div>

              {error && (
                <div className="mt-4 bg-[#FFF5F5] border border-[#FEB2B2] rounded px-4 py-3 text-[#C53030] text-sm">
                  {error}
                </div>
              )}

              {/* OGE Prohibited Overlay */}
              {prohibited && prohibitedInfo && (
                <div className="mt-4 bg-[#D73A49] border-2 border-[#B31D28] rounded-lg p-4">
                  <div className="flex items-start gap-3">
                    <svg className="w-8 h-8 text-white flex-shrink-0 mt-1" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M13.477 14.89A6 6 0 015.11 6.524l8.367 8.368zm1.414-1.414L6.524 5.11a6 6 0 018.367 8.367zM18 10a8 8 0 11-16 0 8 8 0 0116 0z" clipRule="evenodd" />
                    </svg>
                    <div className="flex-1">
                      <h3 className="text-white font-bold text-lg mb-2">
                        ⛔ PROHIBITED — Trading Blocked
                      </h3>
                      <p className="text-white text-sm mb-2">
                        This security is restricted under STOCK Act / OGE regulations.
                      </p>
                      <p className="text-[#FFDCE0] text-sm">
                        <strong>Reason:</strong> {prohibitedInfo.reason}
                      </p>
                      <p className="text-[#FFDCE0] text-sm mt-1">
                        <strong>Sector:</strong> {prohibitedInfo.sector}
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {/* Quote Display */}
              {searchResult && !prohibited && (
                <div className="mt-4 border-t border-[#E1E4E8] pt-4">
                  <div className="flex items-start justify-between mb-4">
                    <div>
                      <h3 className="text-2xl font-bold text-[#24292E] font-mono">
                        {searchResult.symbol}
                      </h3>
                      <p className="text-sm text-[#586069]">
                        {searchResult.name || 'Security Name'}
                      </p>
                    </div>
                    <button
                      onClick={() => addToWatchlist(searchResult.symbol)}
                      disabled={watchlist.some(w => w.symbol === searchResult.symbol)}
                      className="px-3 py-1 text-sm border border-[#D1D5DB] text-[#586069] hover:bg-[#F6F8FA] disabled:opacity-50 rounded transition-colors"
                    >
                      {watchlist.some(w => w.symbol === searchResult.symbol) ? '⭐ Watching' : '+ Watchlist'}
                    </button>
                  </div>

                  <div className="grid grid-cols-3 gap-4">
                    <div>
                      <p className="text-xs text-[#586069] mb-1">Last Price</p>
                      <p className="text-2xl font-bold text-[#24292E]">
                        {formatCurrency(searchResult.last || searchResult.price || 0)}
                      </p>
                    </div>
                    <div>
                      <p className="text-xs text-[#586069] mb-1">Change</p>
                      <p className={`text-lg font-semibold ${
                        (searchResult.change || 0) >= 0 ? 'text-[#28A745]' : 'text-[#D73A49]'
                      }`}>
                        {(searchResult.change || 0) >= 0 ? '+' : ''}{formatCurrency(searchResult.change || 0)}
                      </p>
                    </div>
                    <div>
                      <p className="text-xs text-[#586069] mb-1">Volume</p>
                      <p className="text-lg font-semibold text-[#24292E]">
                        {formatNumber(searchResult.volume || 0)}
                      </p>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Order Entry */}
            {searchResult && !prohibited && (
              <div className="bg-white rounded-lg border border-[#E1E4E8] p-6">
                <h2 className="text-lg font-semibold text-[#24292E] mb-4">Place Order</h2>

                <div className="space-y-4">
                  {/* Side */}
                  <div>
                    <label className="block text-sm font-medium text-[#24292E] mb-2">
                      Order Side
                    </label>
                    <div className="flex gap-3">
                      <button
                        onClick={() => setOrderSide('buy')}
                        className={`flex-1 px-4 py-3 rounded border font-semibold transition-colors ${
                          orderSide === 'buy'
                            ? 'bg-[#28A745] text-white border-[#28A745]'
                            : 'bg-white text-[#586069] border-[#D1D5DB] hover:bg-[#F6F8FA]'
                        }`}
                      >
                        Buy
                      </button>
                      <button
                        onClick={() => setOrderSide('sell')}
                        className={`flex-1 px-4 py-3 rounded border font-semibold transition-colors ${
                          orderSide === 'sell'
                            ? 'bg-[#D73A49] text-white border-[#D73A49]'
                            : 'bg-white text-[#586069] border-[#D1D5DB] hover:bg-[#F6F8FA]'
                        }`}
                      >
                        Sell
                      </button>
                    </div>
                  </div>

                  {/* Quantity */}
                  <div>
                    <label className="block text-sm font-medium text-[#24292E] mb-2">
                      Quantity
                    </label>
                    <input
                      type="number"
                      value={orderQuantity}
                      onChange={(e) => setOrderQuantity(e.target.value)}
                      min="0"
                      step="1"
                      placeholder="Number of shares"
                      className="w-full px-4 py-3 border border-[#D1D5DB] rounded text-lg focus:outline-none focus:ring-2 focus:ring-[#0366D6] focus:border-transparent"
                    />
                  </div>

                  {/* Order Type */}
                  <div>
                    <label className="block text-sm font-medium text-[#24292E] mb-2">
                      Order Type
                    </label>
                    <select
                      value={orderType}
                      onChange={(e) => setOrderType(e.target.value)}
                      className="w-full px-4 py-3 border border-[#D1D5DB] rounded focus:outline-none focus:ring-2 focus:ring-[#0366D6] focus:border-transparent"
                    >
                      <option value="market">Market Order</option>
                      <option value="limit">Limit Order</option>
                    </select>
                  </div>

                  {/* Limit Price (if limit order) */}
                  {orderType === 'limit' && (
                    <div>
                      <label className="block text-sm font-medium text-[#24292E] mb-2">
                        Limit Price
                      </label>
                      <div className="relative">
                        <span className="absolute left-3 top-3 text-[#586069]">$</span>
                        <input
                          type="number"
                          value={limitPrice}
                          onChange={(e) => setLimitPrice(e.target.value)}
                          min="0"
                          step="0.01"
                          placeholder="0.00"
                          className="w-full pl-8 pr-4 py-3 border border-[#D1D5DB] rounded text-lg focus:outline-none focus:ring-2 focus:ring-[#0366D6] focus:border-transparent"
                        />
                      </div>
                    </div>
                  )}

                  {/* Time in Force */}
                  <div>
                    <label className="block text-sm font-medium text-[#24292E] mb-2">
                      Time in Force
                    </label>
                    <select
                      value={timeInForce}
                      onChange={(e) => setTimeInForce(e.target.value)}
                      className="w-full px-4 py-3 border border-[#D1D5DB] rounded focus:outline-none focus:ring-2 focus:ring-[#0366D6] focus:border-transparent"
                    >
                      <option value="day">Day</option>
                      <option value="gtc">Good 'Til Canceled (GTC)</option>
                      <option value="ioc">Immediate or Cancel (IOC)</option>
                    </select>
                  </div>

                  {/* Estimated Total */}
                  {orderQuantity && searchResult && (
                    <div className="bg-[#F6F8FA] rounded-lg p-4">
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-[#586069]">Estimated Total</span>
                        <span className="text-xl font-bold text-[#24292E]">
                          {formatCurrency(
                            parseFloat(orderQuantity) * 
                            (orderType === 'limit' && limitPrice 
                              ? parseFloat(limitPrice) 
                              : (searchResult.last || searchResult.price || 0))
                          )}
                        </span>
                      </div>
                    </div>
                  )}

                  {/* Place Order Button */}
                  <button
                    onClick={handlePlaceOrder}
                    disabled={loading || !orderQuantity}
                    className="w-full px-6 py-4 bg-[#0366D6] hover:bg-[#0256C5] disabled:bg-[#D1D5DB] text-white font-bold text-lg rounded transition-colors"
                  >
                    {loading ? 'Processing...' : `Place ${orderSide.toUpperCase()} Order`}
                  </button>
                </div>
              </div>
            )}

            {/* Recent Orders */}
            <div className="bg-white rounded-lg border border-[#E1E4E8] p-6">
              <h2 className="text-lg font-semibold text-[#24292E] mb-4">Recent Tier 2 Orders</h2>
              
              {recentOrders.length === 0 ? (
                <p className="text-center text-[#586069] py-8">No recent orders</p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="bg-[#F6F8FA] border-b border-[#E1E4E8]">
                        <th className="text-left py-2 px-3 font-medium text-[#24292E]">Date</th>
                        <th className="text-left py-2 px-3 font-medium text-[#24292E]">Symbol</th>
                        <th className="text-center py-2 px-3 font-medium text-[#24292E]">Side</th>
                        <th className="text-right py-2 px-3 font-medium text-[#24292E]">Qty</th>
                        <th className="text-center py-2 px-3 font-medium text-[#24292E]">Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {recentOrders.map((order, idx) => (
                        <tr key={idx} className="border-b border-[#E1E4E8]">
                          <td className="py-2 px-3 text-[#586069]">{formatDate(order.created_at)}</td>
                          <td className="py-2 px-3 font-mono font-semibold text-[#0366D6]">{order.symbol}</td>
                          <td className="py-2 px-3 text-center">
                            <span className={`text-xs font-semibold uppercase ${
                              order.side === 'buy' ? 'text-[#28A745]' : 'text-[#D73A49]'
                            }`}>
                              {order.side}
                            </span>
                          </td>
                          <td className="py-2 px-3 text-right">{order.quantity}</td>
                          <td className="py-2 px-3 text-center">
                            <span className="px-2 py-1 bg-[#E6F7E6] text-[#28A745] text-xs rounded">
                              {order.status}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>

          {/* Right Column - Watchlist */}
          <div>
            <div className="bg-white rounded-lg border border-[#E1E4E8] p-6 sticky top-6">
              <h2 className="text-lg font-semibold text-[#24292E] mb-4">Watchlist</h2>
              
              {watchlist.length === 0 ? (
                <p className="text-center text-[#586069] py-8 text-sm">
                  Search for securities and add them to your watchlist
                </p>
              ) : (
                <div className="space-y-3">
                  {watchlist.map((item) => (
                    <div key={item.symbol} className="flex items-center justify-between p-3 bg-[#F6F8FA] rounded">
                      <div>
                        <p className="font-mono font-semibold text-[#24292E]">{item.symbol}</p>
                        <p className="text-xs text-[#586069]">Live prices coming soon</p>
                      </div>
                      <button
                        onClick={() => removeFromWatchlist(item.symbol)}
                        className="text-[#D73A49] hover:text-[#B31D28] transition-colors"
                        title="Remove from watchlist"
                      >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* OGE Warning Modal */}
      {showWarningModal && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="fixed inset-0 bg-black bg-opacity-50" />
          
          <div className="flex min-h-full items-center justify-center p-4">
            <div className="relative bg-white rounded-lg shadow-xl max-w-2xl w-full">
              <div className="border-b border-[#E1E4E8] px-6 py-4">
                <h3 className="text-xl font-bold text-[#B8860B]">⚠️ OGE Compliance Warning</h3>
              </div>

              <div className="px-6 py-6">
                <div className="bg-[#FFF9E6] border border-[#B8860B] rounded-lg p-4 mb-4">
                  <p className="text-sm text-[#8B6914] mb-3">
                    The following compliance warnings were flagged for this order. You may proceed, but these warnings will be recorded in your compliance file.
                  </p>
                  <ul className="space-y-2">
                    {ogeWarnings.map((warning, idx) => (
                      <li key={idx} className="text-sm text-[#8B6914] flex items-start gap-2">
                        <span className="text-[#B8860B] flex-shrink-0">•</span>
                        <span>{warning}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                <p className="text-sm text-[#586069]">
                  By proceeding, you acknowledge these warnings and certify that this transaction does not violate your official duties or create a conflict of interest.
                </p>
              </div>

              <div className="border-t border-[#E1E4E8] px-6 py-4 flex gap-3 justify-end">
                <button
                  onClick={() => {
                    setShowWarningModal(false);
                    setPendingOrder(null);
                    setOgeWarnings([]);
                  }}
                  className="px-6 py-2 border border-[#D1D5DB] text-[#586069] hover:bg-[#F6F8FA] rounded transition-colors"
                >
                  Cancel Order
                </button>
                <button
                  onClick={handleAcknowledgeWarnings}
                  className="px-6 py-2 bg-[#B8860B] hover:bg-[#9A7209] text-white font-semibold rounded transition-colors"
                >
                  Acknowledge & Proceed
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
